import { css } from '@emotion/react';
import type { Row, TableInstance } from 'react-table';
import { theme } from '@frontend/theme';
import { Checkbox, Text } from '../../';
import { useControlledField } from '../../';
import { useThemeValues } from '../../../hooks';
import { TableData } from '../table-data-type';

type SelectAllRowsProps<T extends TableData> = {
  tableInstance: TableInstance<T>;
  nonDisabledRows: Row<T>[];
};

export const SelectAllRows = <T extends TableData>({ tableInstance, nonDisabledRows }: SelectAllRowsProps<T>) => {
  const { selectedFlatRows, isMobile, flatRows, tableTrackingIds, rowSelectionConfig, state } = tableInstance;
  const { spacing } = useThemeValues();

  const selectedCount = Object.keys(state.selectedRowIds).length;
  const { selectAllLabel, onSelectAllLabelClick } = rowSelectionConfig || {};

  const allSelected = nonDisabledRows.every((row) => row.isSelected);
  const allUnselected = nonDisabledRows.every((row) => !row.isSelected);

  const selectAllProps = useControlledField({
    type: 'checkbox',
    value: !allUnselected,
    onChange: () => nonDisabledRows.forEach((row) => tableInstance.toggleRowSelected(row.id, !allSelected)),
  });

  const hasDisabledRows = nonDisabledRows.length !== flatRows.length;
  const checkboxFieldLabel = hasDisabledRows ? 'Select All Available' : 'Select All';

  const labelContent =
    typeof selectAllLabel === 'function'
      ? selectAllLabel({ selectedCount, tableInstance })
      : selectAllLabel || checkboxFieldLabel;

  const showLabel = !isMobile && (!selectedFlatRows.length || !!selectAllLabel);

  return (
    <>
      <Checkbox
        data-trackingid={tableTrackingIds.selectAll}
        disabled={tableInstance.isLoading || !flatRows.length || !nonDisabledRows.length}
        name='select-all-rows'
        indeterminate={!allSelected}
        {...selectAllProps}
        error={false}
      />
      {showLabel && (
        <Text
          css={css`
            margin: 0;
            margin-left: ${spacing(2)};
            ${onSelectAllLabelClick &&
            selectedCount &&
            css`
              cursor: pointer;
              text-decoration: underline;
              color: ${theme.colors.primary50};
            `}
          `}
          size='medium'
          color={tableInstance.isLoading || !flatRows.length || !nonDisabledRows.length ? 'disabled' : 'default'}
          onClick={onSelectAllLabelClick ? () => onSelectAllLabelClick({ selectedCount, tableInstance }) : undefined}
          {...(onSelectAllLabelClick && { role: 'button', tabIndex: 0 })}
        >
          {labelContent}
        </Text>
      )}
    </>
  );
};
